// ─────────────────────────────────────────────────────────────
//  Özel Takvimim — Regl & Kadın Sağlığı Takip Modülü
//  Tüm veriler sadece kullanıcıya görünür — hiçbir aile üyesi,
//  admin veya süper admin erişemez.
// ─────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useRef, useState } from "react";
import AsyncActionButton from "../components/AsyncActionButton";
import { CycleGlyph, MoodIcon } from "../components/cycle/CycleIcons";
import { useLanguage } from "../i18n/LanguageContext";
import {
  getCycleSettings, saveCycleSettings,
  getCyclePredictions, getCyclePeriods,
  startPeriod, endPeriod, deletePeriod,
  getDayEntry, saveDayEntry,
  getCalendarMonth, getCycleAnalysis,
  getCycleInsights, getCycleMedications,
  addCycleMedication, updateCycleMedication, deleteCycleMedication,
  exportCycleData, deleteCycleData,
} from "../services/cycleService";
import aileAsistaniAsset from "../assets/aileasistaniasset.png";
import healthPrivateCalendarAsset from "../assets/saglik_ozel-takvimim.png";
import "../styles/pages/cycle.css";
import { healthTextLimitForField, limitHealthText } from "../utils/healthTextLimits";
import { readSensitiveItem, writeSensitiveItem } from "../utils/sensitiveSessionStorage";

// ── Sabitler ─────────────────────────────────────────────────
const PHASES = {
  period:       { key: "period",       color: "#ef476f", label: { tr: "Regl",           en: "Period"        }, angle: 30  },
  post_period:  { key: "post_period",  color: "#f59e0b", label: { tr: "Regl Sonrası",   en: "Post-Period"   }, angle: 90  },
  fertile:      { key: "fertile",      color: "#22c55e", label: { tr: "Doğurgan Dönem", en: "Fertile"       }, angle: 150 },
  ovulation:    { key: "ovulation",    color: "#8b5cf6", label: { tr: "Yumurtlama",     en: "Ovulation"     }, angle: 185 },
  post_ovulation:{ key:"post_ovulation",color:"#06b6d4", label: { tr: "Yumurtlama Sonrası", en: "Post-Ovulation" }, angle: 210 },
  luteal:       { key: "luteal",       color: "#6366f1", label: { tr: "Luteal Faz",     en: "Luteal Phase"  }, angle: 270 },
  pms:          { key: "pms",          color: "#ec4899", label: { tr: "PMS",             en: "PMS"           }, angle: 330 },
  unknown:      { key: "unknown",      color: "#64748b", label: { tr: "Bilinmiyor",      en: "Unknown"       }, angle: 0   },
};

const SYMPTOMS_LIST = [
  { key: "cramps",           tr: "Karın ağrısı",      en: "Cramps"           },
  { key: "groin_pain",       tr: "Kasık ağrısı",      en: "Groin pain"       },
  { key: "back_pain",        tr: "Bel ağrısı",        en: "Back pain"        },
  { key: "headache",         tr: "Baş ağrısı",        en: "Headache"         },
  { key: "migraine",         tr: "Migren",            en: "Migraine"         },
  { key: "bloating",         tr: "Şişkinlik",         en: "Bloating"         },
  { key: "nausea",           tr: "Bulantı",           en: "Nausea"           },
  { key: "fatigue",          tr: "Halsizlik",         en: "Fatigue"          },
  { key: "sleepiness",       tr: "Uyku hali",         en: "Sleepiness"       },
  { key: "breast_tenderness",tr: "Göğüs hassasiyeti", en: "Breast tenderness"},
  { key: "mood_swings",      tr: "Ruh hali değişimi", en: "Mood swings"      },
  { key: "anxiety",          tr: "Kaygı",             en: "Anxiety"          },
  { key: "irritability",     tr: "Sinirlilik",        en: "Irritability"     },
  { key: "acne",             tr: "Sivilce",           en: "Acne"             },
  { key: "increased_appetite",tr:"İştah artışı",      en: "Increased appetite"},
  { key: "decreased_appetite",tr:"İştahsızlık",       en: "Decreased appetite"},
  { key: "discharge",        tr: "Akıntı",            en: "Discharge"        },
  { key: "fever",            tr: "Ateş",              en: "Fever"            },
  { key: "dizziness",        tr: "Baş dönmesi",       en: "Dizziness"        },
];

const MOODS = [
  { key: "great",    tr: "Harika",   en: "Great"    },
  { key: "good",     tr: "İyi",      en: "Good"     },
  { key: "normal",   tr: "Normal",   en: "Normal"   },
  { key: "sensitive",tr: "Hassas",   en: "Sensitive"},
  { key: "sad",      tr: "Üzgün",    en: "Sad"      },
  { key: "tense",    tr: "Gergin",   en: "Tense"    },
  { key: "anxious",  tr: "Kaygılı",  en: "Anxious"  },
];

const DISCHARGE_TYPES = [
  { key: "none",        tr: "Yok",      en: "None"        },
  { key: "clear",       tr: "Şeffaf",   en: "Clear"       },
  { key: "white",       tr: "Beyaz",    en: "White"       },
  { key: "creamy",      tr: "Kremsi",   en: "Creamy"      },
  { key: "thick",       tr: "Yoğun",    en: "Thick"       },
  { key: "bloody",      tr: "Kanlı",    en: "Bloody"      },
  { key: "other",       tr: "Diğer",    en: "Other"       },
];

const FLOW_INTENSITY = [
  { key: "light",    tr: "Hafif",     en: "Light"     },
  { key: "normal",   tr: "Normal",    en: "Normal"    },
  { key: "heavy",    tr: "Yoğun",     en: "Heavy"     },
  { key: "very_heavy",tr:"Çok yoğun", en: "Very heavy"},
];

const pad2 = (n) => String(n).padStart(2, "0");
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};
const toYMD = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    const iso = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (iso) return iso[1];
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};
const parseYMD = (value) => {
  const ymd = toYMD(value);
  if (!ymd) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const addDaysYMD = (value, days) => {
  const d = parseYMD(value);
  if (!d) return "";
  d.setDate(d.getDate() + days);
  return toYMD(d);
};

const CYCLE_PAGE_CACHE_KEY = "cycle_page_cache_v2";
let cyclePageMemoryCache = null;

const readCyclePageCache = () => {
  if (cyclePageMemoryCache) return cyclePageMemoryCache;
  try {
    const raw = readSensitiveItem(CYCLE_PAGE_CACHE_KEY);
    cyclePageMemoryCache = raw ? JSON.parse(raw) : null;
  } catch (_) {
    cyclePageMemoryCache = null;
  }
  return cyclePageMemoryCache || null;
};

const writeCyclePageCache = (patch = {}) => {
  const next = { ...(readCyclePageCache() || {}), ...patch, loaded: true, savedAt: Date.now() };
  cyclePageMemoryCache = next;
  try { writeSensitiveItem(CYCLE_PAGE_CACHE_KEY, JSON.stringify(next)); } catch (_) {}
  return next;
};

const CYCLE_ANALYSIS_CACHE_KEY = "cycle_analysis_cache_v1";
let cycleAnalysisMemoryCache = {};
try {
  cycleAnalysisMemoryCache = JSON.parse(readSensitiveItem(CYCLE_ANALYSIS_CACHE_KEY) || "{}") || {};
} catch (_) {
  cycleAnalysisMemoryCache = {};
}
const writeCycleAnalysisCache = (range, data) => {
  if (!range || !data) return;
  cycleAnalysisMemoryCache = { ...cycleAnalysisMemoryCache, [range]: { data, savedAt: Date.now() } };
  try { writeSensitiveItem(CYCLE_ANALYSIS_CACHE_KEY, JSON.stringify(cycleAnalysisMemoryCache)); } catch (_) {}
};

const normalizeDateKey = (value) => {
  // Takvimde bir gün geri işaretleme hatasını önlemek için UTC/toISOString kullanma.
  // PostgreSQL DATE değerleri cihazın lokal günüyle normalize edilir.
  if (!value) return "";
  if (typeof value === "string") {
    const iso = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (iso) return iso[1];
  }
  return toYMD(value);
};

const friendlyCycleError = (error, lang) => {
  if (!error) return lang === "en" ? "The action could not be completed." : "İşlem tamamlanamadı.";
  const msg = String(error).toLowerCase();
  if (msg.includes("timeout")) return lang === "en" ? "The server did not respond. Please try again." : "Sunucu yanıt vermedi. Lütfen tekrar deneyin.";
  if (msg.includes("no_active_period") || msg.includes("open period") || msg.includes("aktif regl")) return lang === "en" ? "No active period found. Please save a period start first." : "Aktif regl bulunamadı. Önce regl başlangıcı kaydedin.";
  if (msg.includes("end_before_start")) return lang === "en" ? "End date cannot be before the start date." : "Bitiş tarihi başlangıç tarihinden önce olamaz.";
  if (msg.includes("duplicate_period")) return lang === "en" ? "A period record already exists for this date." : "Bu tarih için zaten bir regl kaydı bulunuyor.";
  if (msg.includes("overlapping_period")) return lang === "en" ? "These dates overlap another period record." : "Bu tarihler başka bir regl kaydıyla çakışıyor.";
  return lang === "en" ? "Save failed. Please check and try again." : "Kayıt başarısız. Lütfen kontrol edip tekrar deneyin.";
};

// ── Yardımcı fonksiyonlar ─────────────────────────────────────
function formatDate(dateStr, lang) {
  if (!dateStr) return "–";
  const d = parseYMD(dateStr);
  if (lang === "en") {
    return d.toLocaleDateString("en-US", { day: "numeric", month: "short" });
  }
  return d.toLocaleDateString("tr-TR", { day: "numeric", month: "long" });
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const diff = Math.round((parseYMD(dateStr) - new Date().setHours(0,0,0,0)) / 86400000);
  return diff;
}

function daysFromStart(dateStr) {
  if (!dateStr) return 1;
  const diff = Math.round((new Date().setHours(0,0,0,0) - parseYMD(dateStr)) / 86400000);
  return Math.max(1, diff + 1);
}

// ── Onboarding ────────────────────────────────────────────────
function CycleOnboarding({ onComplete, t, lang }) {
  const [step, setStep] = useState(1);
  const [lastPeriod, setLastPeriod] = useState(today());
  const [cycleLen, setCycleLen] = useState(28);
  const [periodLen, setPeriodLen] = useState(5);
  const [saving, setSaving] = useState(false);
  const [setupError, setSetupError] = useState("");

  const finish = async () => {
    setSaving(true);
    setSetupError("");
    try {
      const periodResult = await startPeriod({ start_date: lastPeriod, flow_intensity: "normal" });
      if (periodResult?.ok === false && !["duplicate_period", "overlapping_period"].includes(periodResult?.error)) {
        throw new Error(periodResult?.error || "period_setup_failed");
      }
      const settingsResult = await saveCycleSettings({
        last_period_start: lastPeriod,
        avg_cycle_length: cycleLen,
        avg_period_length: periodLen,
        setup_completed: true,
      });
      if (settingsResult?.ok === false) throw new Error(settingsResult?.error || "settings_setup_failed");
      onComplete();
    } catch (e) {
      setSetupError(friendlyCycleError(e.message, lang));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cycleOnboarding">
      <div className="cycleOnboardingCard">
        <div className="cycleOnboardingBrand" aria-hidden="true">
          <span className="cycleOnboardingIcon"><CycleGlyph name="drop" size={28} /></span>
          <span className="cycleOnboardingBrandRing" />
        </div>
        <div className="cycleOnboardingSteps">
          {[1,2,3].map(i => (
            <div key={i} className={`cycleOnboardingDot ${step === i ? "active" : step > i ? "done" : ""}`} />
          ))}
        </div>
        <div className="cycleOnboardingStepLabel">{step} / 3</div>

        {step === 1 && (
          <>
            <h2>{t("cycle.onb.welcome")}</h2>
            <p>{t("cycle.onb.welcomeDesc")}</p>
            <div className="cycleOnboardingField">
              <label>{t("cycle.onb.lastPeriod")}</label>
              <input type="date" value={lastPeriod} max={today()}
                onChange={e => setLastPeriod(e.target.value)} className="cycleInput" />
            </div>
            <div className="cycleOnboardingSingleAction"><button className="cyclePrimaryBtn" onClick={() => setStep(2)}>{t("common.next") || "İleri"}<span aria-hidden="true">→</span></button></div>
          </>
        )}
        {step === 2 && (
          <>
            <h2>{t("cycle.onb.cycleLength")}</h2>
            <p>{t("cycle.onb.cycleLengthDesc")}</p>
            <div className="cycleOnboardingField">
              <label><span>{t("cycle.onb.avgCycle")}</span><strong>{cycleLen} {t("cycle.days")}</strong></label>
              <input type="range" min="21" max="45" value={cycleLen}
                onChange={e => setCycleLen(Number(e.target.value))} className="cycleSlider" />
              <div className="cycleSliderHints"><span>21</span><span>45</span></div>
            </div>
            <div className="cycleOnboardingField">
              <label><span>{t("cycle.onb.avgPeriod")}</span><strong>{periodLen} {t("cycle.days")}</strong></label>
              <input type="range" min="2" max="10" value={periodLen}
                onChange={e => setPeriodLen(Number(e.target.value))} className="cycleSlider" />
              <div className="cycleSliderHints"><span>2</span><span>10</span></div>
            </div>
            <div className="cycleOnboardingBtns">
              <button className="cycleSecondaryBtn" onClick={() => setStep(1)}>{t("common.back") || "Geri"}</button>
              <button className="cyclePrimaryBtn" onClick={() => setStep(3)}>{t("common.next") || "İleri"}<span aria-hidden="true">→</span></button>
            </div>
          </>
        )}
        {step === 3 && (
          <>
            <h2>{t("cycle.onb.privacy")}</h2>
            <p>{t("cycle.onb.privacyDesc")}</p>
            <div className="cycleOnboardingPrivacy">
              <div className="cyclePrivacyItem"><span><CycleGlyph name="shield" size={19} /></span>{t("cycle.privacy.familyHide")}</div>
              <div className="cyclePrivacyItem"><span><CycleGlyph name="shield" size={19} /></span>{t("cycle.privacy.adminHide")}</div>
              <div className="cyclePrivacyItem"><span><CycleGlyph name="shield" size={19} /></span>{t("cycle.privacy.superAdminHide")}</div>
              <div className="cyclePrivacyItem positive"><span><CycleGlyph name="check" size={19} /></span>{t("cycle.privacy.onlyYou")}</div>
            </div>
            <div className="cycleOnboardingBtns">
              <button className="cycleSecondaryBtn" onClick={() => setStep(2)}>{t("common.back") || "Geri"}</button>
              <AsyncActionButton className="cyclePrimaryBtn" externalBusy={saving} onAction={finish}>{t("cycle.onb.start")}</AsyncActionButton>
            </div>
            {setupError && <div className="cycleMedError">{setupError}</div>}
          </>
        )}
      </div>
    </div>
  );
}

// ── Phase Ring ────────────────────────────────────────────────
function CyclePhaseRing({ phase, cycleDay, cycleLen, t, lang }) {
  const phaseData = PHASES[phase] || PHASES.unknown;
  const progress = cycleLen ? Math.min(1, (cycleDay - 1) / cycleLen) : 0;
  const circumference = 2 * Math.PI * 54;
  const dashOffset = circumference * (1 - progress);

  return (
    <div className="cyclePhaseRing">
      <svg width="140" height="140" viewBox="0 0 140 140">
        {/* Arka plan halkası */}
        <circle cx="70" cy="70" r="54" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="10" />
        {/* Progress halkası */}
        <circle
          cx="70" cy="70" r="54"
          fill="none"
          stroke={phaseData.color}
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          style={{ transform: "rotate(-90deg)", transformOrigin: "70px 70px", transition: "stroke-dashoffset 0.8s ease" }}
        />
        {/* Merkez bilgi */}
        <text x="70" y="62" textAnchor="middle" className="cycleRingDay">{cycleDay || "–"}</text>
        <text x="70" y="80" textAnchor="middle" className="cycleRingDayLabel">{t("cycle.day")}</text>
        <text x="70" y="96" textAnchor="middle" className="cycleRingPhase" fill={phaseData.color}>
          {phaseData.label[lang] || phaseData.label.tr}
        </text>
      </svg>
    </div>
  );
}

// ── Hero Card ─────────────────────────────────────────────────
function CycleHeroCard({ predictions, settings, onQuickAction, t, lang }) {
  // predictions null ise varsayılan boş nesne kullan — spinner gösterme
  const pred = predictions || {
    currentCycleDay: null, currentPhase: "unknown",
    nextPeriodDate: null, ovulationDate: null,
    fertileStart: null, fertileEnd: null,
    regularityScore: null, avgCycleLength: 28,
    avgPeriodLength: 5, lastPeriodStart: null, delayDays: 0,
  };

  const { currentCycleDay, currentPhase, nextPeriodDate, ovulationDate,
          fertileStart, fertileEnd, regularityScore, avgCycleLength,
          avgPeriodLength, lastPeriodStart, delayDays } = pred;

  const daysToNext = daysUntil(nextPeriodDate);
  const confidence = pred.confidence || (pred.periodCount >= 3 ? "high" : pred.periodCount >= 2 ? "medium" : "starter");
  const confidenceLabel = t(`cycle.confidence.${confidence}`);
  const activePeriod = pred.activePeriod === true;

  return (
    <div className={`cycleHeroCard phase-${currentPhase || "unknown"}`}>
      <div className="cycleHeroAmbient" aria-hidden="true" />
      <div className="cycleHeroEyebrow">
        <span className="cyclePrivacyPill"><CycleGlyph name="shield" size={14} /> {t("cycle.private")}</span>
        <span className={`cycleConfidence ${confidence}`}>{confidenceLabel}</span>
      </div>
      <div className="cycleHeroTop">
        <CyclePhaseRing
          phase={currentPhase || "unknown"}
          cycleDay={currentCycleDay || 1}
          cycleLen={avgCycleLength || 28}
          t={t} lang={lang}
        />
        <div className="cycleHeroInfo">
          <div className="cycleHeroTitle">{activePeriod ? t("cycle.hero.activePeriod") : t("cycle.hero.today")}</div>
          <div className="cycleHeroDay">{currentCycleDay || "–"}. {t("cycle.day")}</div>
          <div className="cycleHeroPhaseName">{PHASES[currentPhase]?.label?.[lang] || PHASES.unknown.label[lang]}</div>
          {delayDays > 3 && (
            <div className="cycleDelayBadge">⚠ {delayDays} {t("cycle.daysLate")}</div>
          )}
          <div className="cycleHeroMeta">
            <div className="cycleHeroMetaItem">
              <span>{t("cycle.hero.nextPeriod")}</span>
              <b style={{ color: "#ef476f" }}>
                {nextPeriodDate ? (daysToNext === 0 ? t("cycle.today") : daysToNext > 0 ? `${formatDate(nextPeriodDate, lang)} (${daysToNext} ${t("cycle.days")})` : t("cycle.late")) : "–"}
              </b>
            </div>
            <div className="cycleHeroMetaItem">
              <span>{t("cycle.hero.ovulation")}</span>
              <b style={{ color: "#8b5cf6" }}>{ovulationDate ? formatDate(ovulationDate, lang) : "–"}</b>
            </div>
            <div className="cycleHeroMetaItem">
              <span>{t("cycle.hero.fertile")}</span>
              <b style={{ color: "#22c55e" }}>
                {fertileStart && fertileEnd ? `${formatDate(fertileStart, lang)} – ${formatDate(fertileEnd, lang)}` : "–"}
              </b>
            </div>
            {regularityScore !== null && (
              <div className="cycleHeroMetaItem">
                <span>{t("cycle.hero.regularity")}</span>
                <b style={{ color: regularityScore >= 80 ? "#22c55e" : regularityScore >= 60 ? "#f59e0b" : "#ef4444" }}>
                  %{regularityScore}
                </b>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="cyclePredictionDeck" aria-label={t("cycle.prediction.title")}>
        <div className="cyclePredictionCard period">
          <span className="cyclePredictionIcon"><CycleGlyph name="drop" /></span>
          <div><small>{t("cycle.hero.nextPeriod")}</small><strong>{nextPeriodDate ? (daysToNext === 0 ? t("cycle.today") : daysToNext > 0 ? `${formatDate(nextPeriodDate, lang)} · ${daysToNext} ${t("cycle.days")}` : t("cycle.late")) : "–"}</strong></div>
        </div>
        <div className="cyclePredictionCard ovulation">
          <span className="cyclePredictionIcon"><CycleGlyph name="spark" /></span>
          <div><small>{t("cycle.hero.ovulation")}</small><strong>{ovulationDate ? formatDate(ovulationDate, lang) : "–"}</strong></div>
        </div>
        <div className="cyclePredictionCard fertile">
          <span className="cyclePredictionIcon"><CycleGlyph name="calendar" /></span>
          <div><small>{t("cycle.hero.fertile")}</small><strong>{fertileStart && fertileEnd ? `${formatDate(fertileStart, lang)} – ${formatDate(fertileEnd, lang)}` : "–"}</strong></div>
        </div>
      </div>

      {/* Hızlı Aksiyonlar */}
      <div className="cycleQuickActions">
        <button className="cycleQABtn cycleQABtnPrimary" onClick={() => onQuickAction(activePeriod ? "end" : "start")}>
          <span className="cycleQAIcon"><CycleGlyph name="drop" /></span><span className="cycleQALabel">{activePeriod ? t("cycle.qa.end") : t("cycle.qa.start")}</span>
        </button>
        <button className="cycleQABtn" onClick={() => onQuickAction("history")}>
          <span className="cycleQAIcon"><CycleGlyph name="calendar" /></span><span className="cycleQALabel">{t("cycle.period.addPast")}</span>
        </button>
        <button className="cycleQABtn" onClick={() => onQuickAction("symptom")}>
          <span className="cycleQAIcon"><CycleGlyph name="spark" /></span><span className="cycleQALabel">{t("cycle.qa.symptom")}</span>
        </button>
        <button className="cycleQABtn" onClick={() => onQuickAction("note")}>
          <span className="cycleQAIcon"><CycleGlyph name="note" /></span><span className="cycleQALabel">{t("cycle.qa.note")}</span>
        </button>
      </div>
    </div>
  );
}

// ── Calendar ──────────────────────────────────────────────────
function CycleCalendar({ year, month, calData, predictions, onDayClick, onMonthChange, t, lang }) {
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDow = new Date(year, month - 1, 1).getDay();
  const offset = firstDow === 0 ? 6 : firstDow - 1;

  const todayStr = today();
  const padded = m => String(m).padStart(2, "0");

  const getDayType = (dayStr) => {
    if (!calData) return null;
    const types = [];

    // Gerçek regl günleri
    for (const p of (calData.periods || [])) {
      const s = p.start_date;
      const e = p.end_date || s;
      if (dayStr >= s && dayStr <= e) { types.push("period"); break; }
    }

    // Tahminler
    if (predictions) {
      const { nextPeriodDate, fertileStart, fertileEnd, ovulationDate, pmsStart, avgPeriodLength } = predictions;
      if (nextPeriodDate && dayStr >= nextPeriodDate) {
        const endEstimate = addDaysYMD(nextPeriodDate, (avgPeriodLength || 5) - 1);
        if (dayStr <= endEstimate) types.push("predicted");
      }
      if (ovulationDate && dayStr === ovulationDate) types.push("ovulation");
      else if (fertileStart && fertileEnd && dayStr >= fertileStart && dayStr <= fertileEnd) types.push("fertile");
      if (pmsStart && nextPeriodDate && dayStr >= pmsStart && dayStr < nextPeriodDate) types.push("pms");
    }

    // Belirti/kayıt girilen günler
    // PostgreSQL DATE → JSON: "2026-05-15" veya "2026-05-15T00:00:00.000Z"
    const entry = (calData.entries || []).find(e => normalizeDateKey(e.entry_date) === dayStr);
    const symptomMap = calData.symptomMap || {};
    const hasSymptoms = Object.keys(symptomMap).some(k => normalizeDateKey(k) === dayStr && (symptomMap[k]?.length > 0));
    const hasNote = !!entry?.notes;
    const hasMood = !!entry?.mood;
    const hasPain = Number(entry?.pain_level || 0) > 0;
    const hasDischarge = !!entry?.discharge_type;
    if (entry || hasSymptoms) types.push("entry");
    if (hasSymptoms) types.push("symptom");
    if (hasNote) types.push("note");
    if (hasMood) types.push("mood");
    if (hasPain) types.push("pain");
    if (hasDischarge) types.push("discharge");

    return types;
  };

  const dayTypeColor = (types) => {
    if (!types || types.length === 0) return null;
    if (types.includes("period")) return "#ef476f";
    if (types.includes("predicted")) return "rgba(239,71,111,0.35)";
    if (types.includes("ovulation")) return "#8b5cf6";
    if (types.includes("fertile")) return "#22c55e";
    if (types.includes("pms")) return "#ec4899";
    return null;
  };

  const locale = lang === "en" ? "en-US" : lang === "tr" ? "tr-TR" : lang;
  const monthTitle = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" })
    .format(new Date(year, month - 1, 1));
  const dayNames = Array.from({ length: 7 }, (_, index) => new Intl.DateTimeFormat(locale, { weekday: "short" })
    .format(new Date(2024, 0, 1 + index)).replace(".", "").slice(0, 2));

  const cells = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const calSwipe = useRef({ x: 0, y: 0 });
  const onCalTouchStart = (e) => {
    calSwipe.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const onCalTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - calSwipe.current.x;
    const dy = Math.abs(e.changedTouches[0].clientY - calSwipe.current.y);
    if (Math.abs(dx) > 70 && dy < 55) onMonthChange(dx < 0 ? 1 : -1);
  };

  return (
    <div className="cycleCal" onTouchStart={onCalTouchStart} onTouchEnd={onCalTouchEnd}>
      <div className="cycleCalHeader">
        <button className="cycleCalNav" onClick={() => onMonthChange(-1)}>‹</button>
        <span className="cycleCalTitle">{monthTitle}</span>
        <button className="cycleCalNav" onClick={() => onMonthChange(1)}>›</button>
      </div>
      <div className="cycleCalDays">
        {dayNames.map(d => <span key={d} className="cycleCalDayName">{d}</span>)}
      </div>
      <div className="cycleCalGrid">
        {cells.map((day, i) => {
          if (!day) return <div key={`e${i}`} className="cycleCalCell empty" />;
          const dayStr = `${year}-${padded(month)}-${padded(day)}`;
          const types = getDayType(dayStr);
          const color = dayTypeColor(types);
          const isToday = dayStr === todayStr;
          const hasEntry = types?.includes("entry") || types?.includes("symptom");
          const typeClasses = (types || []).map(type => `is-${type}`).join(" ");
          const previousDay = day > 1 ? `${year}-${padded(month)}-${padded(day - 1)}` : null;
          const nextDay = day < daysInMonth ? `${year}-${padded(month)}-${padded(day + 1)}` : null;
          const previousTypes = previousDay ? getDayType(previousDay) || [] : [];
          const nextTypes = nextDay ? getDayType(nextDay) || [] : [];
          const primaryRange = ["period", "predicted", "fertile"].find(type => types?.includes(type));
          const startsRange = primaryRange && (!previousTypes.includes(primaryRange) || i % 7 === 0);
          const endsRange = primaryRange && (!nextTypes.includes(primaryRange) || i % 7 === 6);

          return (
            <button
              key={dayStr}
              className={`cycleCalCell ${isToday ? "today" : ""} ${color ? "hasType" : ""} ${typeClasses} ${primaryRange ? `range-${primaryRange}` : ""} ${startsRange ? "range-start" : ""} ${endsRange ? "range-end" : ""}`}
              onClick={() => onDayClick(dayStr)}
            >
              <span className="cycleCalDayNum">{day}</span>
              {hasEntry && <span className="cycleCalEntryBadge" title={t("cycle.calendar.hasEntry")}><CycleGlyph name="note" size={10} /></span>}
              {isToday && <span className="cycleCalTodayDot">{t("cycle.today")}</span>}
            </button>
          );
        })}
      </div>
      {/* Renk Açıklamaları */}
      <div className="cycleCalLegend">
        <span><i style={{ background: "#ef476f" }} />{t("cycle.legend.period")}</span>
        <span><i style={{ background: "rgba(239,71,111,0.35)" }} />{t("cycle.legend.predicted")}</span>
        <span><i style={{ background: "#8b5cf6" }} />{t("cycle.legend.ovulation")}</span>
        <span><i style={{ background: "#22c55e" }} />{t("cycle.legend.fertile")}</span>
        <span><i style={{ background: "#ec4899" }} />{t("cycle.legend.pms")}</span>
      </div>
    </div>
  );
}

// ── Day Detail Panel ──────────────────────────────────────────
function DayDetailPanel({ date, t, lang, onClose, onSaved, settings }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [symptoms, setSymptoms] = useState([]);
  const [moodIdx, setMoodIdx] = useState(-1);
  const [dischargeIdx, setDischargeIdx] = useState(-1);
  const [painLevel, setPainLevel] = useState(0);
  const [notes, setNotes] = useState("");
  const [sexualActivity, setSexualActivity] = useState(null); // null | {protected, pregnancy_intent}

  useEffect(() => {
    if (!date) return;
    setLoading(true);
    getDayEntry(date).then(res => {
      if (res.ok) {
        const e = res.entry;
        setSymptoms(res.symptoms || []);
        const mi = MOODS.findIndex(m => m.key === e?.mood);
        setMoodIdx(mi >= 0 ? mi : -1);
        const di = DISCHARGE_TYPES.findIndex(d => d.key === e?.discharge_type);
        setDischargeIdx(di >= 0 ? di : -1);
        setPainLevel(e?.pain_level || 0);
        setNotes(e?.notes || "");
        setSexualActivity(res.sexual ? {
          protected: res.sexual.protected !== false,
          pregnancy_intent: res.sexual.pregnancy_intent === true,
        } : null);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [date]);

  const toggleSymptom = (key) => {
    setSymptoms(prev => prev.includes(key) ? prev.filter(s => s !== key) : [...prev, key]);
  };

  const [saveError, setSaveError] = useState("");
  const save = async () => {
    setSaving(true);
    setSaveError("");
    try {
      const mood = moodIdx >= 0 ? MOODS[moodIdx].key : null;
      const discharge_type = dischargeIdx >= 0 ? DISCHARGE_TYPES[dischargeIdx].key : null;
      const res = await Promise.race([
        saveDayEntry(date, { mood, discharge_type, pain_level: painLevel, notes, symptoms }),
        new Promise((_, rej) => setTimeout(() => rej(new Error("timeout")), 10000)),
      ]);
      if (res?.ok !== false) { onSaved?.(); onClose(); return true; }
      setSaveError(friendlyCycleError(res?.error, lang));
      return false;
    } catch(e) {
      setSaveError(friendlyCycleError(e.message, lang));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const selectedMood = moodIdx >= 0 ? MOODS[moodIdx] : null;
  const selectedDischarge = dischargeIdx >= 0 ? DISCHARGE_TYPES[dischargeIdx] : null;

  return (
    <div className="cycleDayPanel">
      <div className="cycleDayPanelHeader">
        <span className="cycleDayPanelDate">{formatDate(date, lang)}</span>
        <button className="cycleDayPanelClose" onClick={onClose}>✕</button>
      </div>
      {loading ? (
        <div className="cyclePanelLoading"><div className="cycleSpinner" /></div>
      ) : (
        <div className="cycleDayPanelBody">

          {/* Ruh Hali — slider */}
          <div className="cyclePanelSection">
            <div className="cyclePanelSectionTitle">
              {t("cycle.mood.title")}
              {selectedMood && <span className="cycleSectionValue">{selectedMood[lang] || selectedMood.tr}</span>}
            </div>
            <div className="cycleMoodPicker" role="group" aria-label={t("cycle.mood.title")}>
              {MOODS.map((m, index) => (
                <button key={m.key} type="button" className={`cycleMoodChoice ${moodIdx === index ? "active" : ""}`}
                  aria-pressed={moodIdx === index} onClick={() => setMoodIdx(moodIdx === index ? -1 : index)}>
                  <MoodIcon mood={m.key} size={34} />
                  <span>{m[lang] || m.tr}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Ağrı */}
          <div className="cyclePanelSection">
            <div className="cyclePanelSectionTitle">
              {t("cycle.pain.title")} <span className="cycleSectionValue">{painLevel}/10</span>
            </div>
            <input type="range" min="0" max="10" step="1" value={painLevel}
              onChange={e => setPainLevel(Number(e.target.value))} className="cyclePainSlider" />
            <div className="cycleSliderHints"><span>0</span><span>5</span><span>10</span></div>
          </div>

          {/* Belirtiler — 2 sütun kaydırılabilir grid */}
          <div className="cyclePanelSection">
            <div className="cyclePanelSectionTitle">{t("cycle.symptoms.title")}
              {symptoms.length > 0 && <span className="cycleSectionValue"> ({symptoms.length})</span>}
            </div>
            <div className="cycleSymptomGrid">
              {SYMPTOMS_LIST.map(s => (
                <button key={s.key}
                  className={`cycleSymptomItem ${symptoms.includes(s.key) ? "active" : ""}`}
                  onClick={() => toggleSymptom(s.key)}>
                  <span className="cycleSymptomCheck">{symptoms.includes(s.key) ? "✓" : ""}</span>
                  {s[lang] || s.tr}
                </button>
              ))}
            </div>
          </div>

          {/* Akıntı — slider */}
          <div className="cyclePanelSection">
            <div className="cyclePanelSectionTitle">
              {t("cycle.discharge.title")}
              {selectedDischarge && <span className="cycleSectionValue"> {selectedDischarge[lang] || selectedDischarge.tr}</span>}
            </div>
            <div className="cycleDischargeSliderWrap">
              <input type="range" min="-1" max={DISCHARGE_TYPES.length - 1} step="1"
                value={dischargeIdx}
                onChange={e => setDischargeIdx(Number(e.target.value))}
                className="cycleDischargeSlider" />
              <div className="cycleDischargeLabels">
                {DISCHARGE_TYPES.map(d => (
                  <span key={d.key} className={dischargeIdx === DISCHARGE_TYPES.indexOf(d) ? "active" : ""}>
                    {d[lang] || d.tr}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Not */}
          <div className="cyclePanelSection">
            <div className="cyclePanelSectionTitle">{t("cycle.note.title")}</div>
            <textarea className="cycleNoteInput" placeholder={t("cycle.note.placeholder")}
              value={notes} maxLength={healthTextLimitForField("notes")} onChange={e => setNotes(limitHealthText("notes", e.target.value))} rows={3} />
          </div>

          {/* Cinsel Sağlık — sadece özellik açıksa */}
          {settings?.sexual_tracking_enabled && (
            <div className="cyclePanelSection">
              <div className="cyclePanelSectionTitle">{t("cycle.sexual.title")}</div>
              <div className="cycleSexualGrid">
                <button
                  className={`cycleSexualBtn ${sexualActivity !== null ? "active" : ""}`}
                  onClick={() => setSexualActivity(sexualActivity !== null ? null : { protected: true, pregnancy_intent: false })}>
                  {sexualActivity !== null ? "✓ " : ""}{sexualActivity?.protected === false ? t("cycle.sexual.unprotected") : t("cycle.sexual.protected")}
                </button>
                {sexualActivity !== null && (
                  <>
                    <button
                      className={`cycleSexualBtn ${!sexualActivity.protected ? "active" : ""}`}
                      onClick={() => setSexualActivity(p => ({ ...p, protected: !p.protected }))}>
                      {sexualActivity.protected ? t("cycle.sexual.protected") : t("cycle.sexual.unprotected")}
                    </button>
                    <button
                      className={`cycleSexualBtn ${sexualActivity.pregnancy_intent ? "active" : ""}`}
                      onClick={() => setSexualActivity(p => ({ ...p, pregnancy_intent: !p.pregnancy_intent }))}>
                      {sexualActivity.pregnancy_intent ? "✓ " : ""}{t("cycle.sexual.pregnancyIntent")}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {saveError && <div className="cycleMedError">{saveError}</div>}
          <div className="cycleModalActions"><AsyncActionButton className="cyclePrimaryBtn" externalBusy={saving} onAction={save}>{t("common.save")}</AsyncActionButton></div>
        </div>
      )}
    </div>
  );
}

// ── Period Start Modal ────────────────────────────────────────
function PeriodStartModal({ mode, t, lang, onClose, onSaved }) {
  const [startDate, setStartDate] = useState(today());
  const [endDate, setEndDate] = useState(today());
  const [isOngoing, setIsOngoing] = useState(mode === "start");
  const [flow, setFlow] = useState("normal");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!startDate) return;
    setSaving(true);
    setSaveErr("");
    try {
      let res;
      if (mode !== "end") {
        if (!isOngoing && endDate < startDate) {
          setSaveErr(friendlyCycleError("end_before_start", lang));
          return false;
        }
        res = await startPeriod({ start_date: startDate, end_date: isOngoing ? null : endDate, flow_intensity: flow });
        if (!res?.period) {
          setSaveErr(friendlyCycleError(res?.error || "duplicate_period", lang));
          return false;
        }
      } else {
        // endPeriod: startDate değeri bitiş tarihi olarak gönderilir
        res = await endPeriod(startDate);
        if (res && res.ok === false) {
          setSaveErr(friendlyCycleError("no_active_period", lang));
          setSaving(false);
          return false;
        }
        if (!res?.period && res?.ok !== false) {
          setSaveErr(friendlyCycleError("no_active_period", lang));
          setSaving(false);
          return false;
        }
      }
      onSaved?.();
      onClose();
      return true;
    } catch(e) {
      setSaveErr(friendlyCycleError(e.message, lang));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const [saveErr, setSaveErr] = useState("");
  // 2 yıl öncesine kadar izin ver
  const minDate = new Date(); minDate.setFullYear(minDate.getFullYear() - 2);
  const minStr = minDate.toISOString().slice(0, 10);

  return (
    <div className="cycleModalOverlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="cycleModal">
        <div className="cycleModalHeader cyclePeriodModalHeader">
          <div><span className="cycleModalIcon"><CycleGlyph name="drop" /></span><span>{mode === "end" ? t("cycle.qa.end") : mode === "history" ? t("cycle.period.addPast") : t("cycle.qa.start")}</span></div>
          <button onClick={onClose}>✕</button>
        </div>
        <div className="cycleModalBody">
          <p className="cycleModalLead">{mode === "end" ? t("cycle.period.endHelp") : t("cycle.period.startHelp")}</p>
          <div className="cyclePanelSection">
            <label className="cyclePanelSectionTitle">
              {mode === "end"
                ? (lang === "en" ? "End date" : "Bitiş tarihi")
                : t("cycle.period.startDate")}
            </label>
            <input type="date" value={startDate} min={minStr} max={today()}
              onChange={e => { setStartDate(e.target.value); if (mode === "history") setEndDate(e.target.value); setSaveErr(""); }} className="cycleInput" />
          </div>
          {mode !== "end" && (
            <>
              {mode === "start" && (
                <div className="cyclePeriodStatusPicker">
                  <button type="button" className={isOngoing ? "active" : ""} onClick={() => setIsOngoing(true)}>{t("cycle.period.ongoing")}</button>
                  <button type="button" className={!isOngoing ? "active" : ""} onClick={() => setIsOngoing(false)}>{t("cycle.period.completed")}</button>
                </div>
              )}
              <div className="cyclePanelSection">
                <label className="cyclePanelSectionTitle">{t("cycle.period.endDate")}</label>
                <input type="date" value={endDate} min={startDate} max={today()}
                  disabled={isOngoing} onChange={e => setEndDate(e.target.value)} className="cycleInput" />
                {isOngoing && <small className="cycleFieldHint">{t("cycle.period.ongoingHint")}</small>}
              </div>
              <div className="cyclePanelSection">
                <div className="cyclePanelSectionTitle">{t("cycle.period.flow")}</div>
                <div className="cycleChipGrid">
                  {FLOW_INTENSITY.map(f => (
                    <button key={f.key}
                      type="button"
                      className={`cycleChip ${flow === f.key ? "active" : ""}`}
                      aria-pressed={flow === f.key}
                      onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); setFlow(f.key); setSaveErr(""); }}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setFlow(f.key); setSaveErr(""); }}>
                      <span className={`cycleFlowDrop ${f.key}`}><CycleGlyph name="drop" size={20} /></span>
                      <span>{f[lang] || f.tr}</span>
                      {flow === f.key && <span className="cycleSelectedCheck">✓</span>}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
          {saveErr && <div className="cycleMedError" style={{marginTop:8}}>{saveErr}</div>}
          <div className="cycleModalActions"><AsyncActionButton className="cyclePrimaryBtn" externalBusy={saving} onAction={save}>{t("common.save")}</AsyncActionButton></div>
        </div>
      </div>
    </div>
  );
}

// ── AI Tavsiyeler Section ─────────────────────────────────────
const STATIC_TIPS = [
  { type: "tip", glyph: "calendar", textKey: "cycle.ai.tip1" },
  { type: "tip", glyph: "chart", textKey: "cycle.ai.tip2" },
  { type: "positive", glyph: "spark", textKey: "cycle.ai.tip3" },
  { type: "info", glyph: "drop", textKey: "cycle.ai.tip4" },
  { type: "tip", glyph: "activity", textKey: "cycle.ai.tip5" },
  { type: "info", glyph: "heart", textKey: "cycle.ai.tip6" },
  { type: "warning", glyph: "shield", textKey: "cycle.ai.tip7" },
];

function CycleInsightsSection({ insights, t, predictions }) {
  const colorMap = { positive: "#22c55e", warning: "#f59e0b", alert: "#ef4444", tip: "#8b5cf6", info: "#06b6d4" };
  const allInsights = insights && insights.length > 0 ? insights : [];
  // Her zaman en az 5 öneri göster; fazlası içeride kaydırılabilir.
  const toShow = allInsights.length >= 5 ? allInsights : [
    ...allInsights,
    ...STATIC_TIPS.slice(0, 5 - allInsights.length).map(s => ({ ...s, text: t(s.textKey) })),
  ];

  return (
    <div className="cycleInsights">
      <div className="cycleAiHeader">
        <div className="cycleAiTitle">{t("cycle.ai.title")}</div>
        <div className="cycleAiRight">
          {predictions?.regularityScore != null && (
            <div className="cycleAiBadge">{predictions.regularityScore}%</div>
          )}
          <img className="cycleAiAsset" src={aileAsistaniAsset} alt="" aria-hidden="true" />
        </div>
      </div>
      {toShow.map((ins, i) => (
        <div key={i} className="cycleInsightCard" style={{ borderColor: `${colorMap[ins.type] || "#64748b"}33` }}>
          <span className="cycleInsightIcon" style={{ color: colorMap[ins.type] || "#64748b" }}>{ins.glyph ? <CycleGlyph name={ins.glyph} size={21} /> : ins.icon}</span>
          <p>{ins.text}</p>
        </div>
      ))}
    </div>
  );
}

// ── Medications Section ───────────────────────────────────────
function CycleMedicationsSection({ t, lang }) {
  const [meds, setMeds] = useState([]);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState("");
  const [dosage, setDosage] = useState("");
  const [reminderTime, setReminderTime] = useState("");
  const [saving, setSaving] = useState(false);
  const [medError, setMedError] = useState("");

  useEffect(() => {
    getCycleMedications().then(res => { if (res.ok) setMeds(res.medications || []); });
  }, []);

  const openEdit = (med) => {
    setEditingId(med.id);
    setName(med.name || "");
    setDosage(med.dosage || "");
    setReminderTime(med.reminder_time || "");
    setAdding(true);
  };

  const resetForm = () => {
    setEditingId(null); setName(""); setDosage(""); setReminderTime(""); setAdding(false); setMedError("");
  };

  const add = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setMedError("");
    try {
      let res;
      if (editingId) {
        res = await Promise.race([
          updateCycleMedication(editingId, { name, dosage, reminder_time: reminderTime }),
          new Promise((_, rej) => setTimeout(() => rej(new Error("timeout")), 10000)),
        ]);
        if (res.ok) setMeds(prev => prev.map(m => m.id === editingId ? { ...m, name, dosage, reminder_time: reminderTime } : m));
      } else {
        res = await Promise.race([
          addCycleMedication({ name, dosage, reminder_time: reminderTime }),
          new Promise((_, rej) => setTimeout(() => rej(new Error("timeout")), 10000)),
        ]);
        if (res.ok) setMeds(prev => [res.medication, ...prev]);
      }
      if (res.ok) { resetForm(); return true; }
      setMedError(friendlyCycleError(res.error, lang));
      return false;
    } catch(e) {
      setMedError(friendlyCycleError(e.message, lang));
      return false;
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    await deleteCycleMedication(id);
    setMeds(prev => prev.filter(m => m.id !== id));
  };

  return (
    <div className="cycleMedications">
      <div className="cycleSectionHeader">
        <div className="cycleSectionTitle">{t("cycle.meds.title")}</div>
        <button className="cycleAddBtn" onClick={() => adding ? resetForm() : setAdding(true)}>
          {adding ? "✕" : "+ " + t("cycle.meds.add")}
        </button>
      </div>
      {adding && (
        <div className="cycleMedForm">
          <input className="cycleInput" maxLength={healthTextLimitForField("name")} placeholder={t("cycle.meds.namePlaceholder")} value={name} onChange={e => setName(limitHealthText("name", e.target.value))} />
          <input className="cycleInput" maxLength={healthTextLimitForField("dose")} placeholder={t("cycle.meds.dosagePlaceholder")} value={dosage} onChange={e => setDosage(limitHealthText("dose", e.target.value))} />
          <div className="cycleTimeInputWrap">
            <label className="cycleTimeLabel">{lang === "en" ? "Reminder time" : "Hatırlatma saati"}</label>
            <input type="time" className="cycleInput cycleTimeInput" value={reminderTime}
              placeholder={lang === "en" ? "Select time" : "Saat seçin"}
              onChange={e => setReminderTime(e.target.value)} />
          </div>
          {medError && <div className="cycleMedError">{medError}</div>}
          <AsyncActionButton className="cyclePrimaryBtn" externalBusy={saving} busyText={editingId ? t("common.saving") : undefined} onAction={add}>{editingId ? t("cycle.meds.update") : t("common.save")}</AsyncActionButton>
        </div>
      )}
      {meds.length === 0 && !adding ? (
        <div className="cycleMedEmptyState">
          <div className="cycleMedEmptyIcon">💊</div>
          <div className="cycleMedEmptyTitle">{t("cycle.meds.emptyTitle")}</div>
          <p>{t("cycle.meds.emptyDesc")}</p>
          <div className="cycleMedStarterGrid">
            <div><b>⏰</b><span>{t("cycle.meds.emptyFeature1")}</span></div>
            <div><b>🔒</b><span>{t("cycle.meds.emptyFeature2")}</span></div>
            <div><b>📌</b><span>{t("cycle.meds.emptyFeature3")}</span></div>
          </div>
          <button className="cyclePrimaryBtn" onClick={() => setAdding(true)}>{t("cycle.meds.addFirst")}</button>
        </div>
      ) : meds.length > 0 ? (
        <div className="cycleMedList">
          {meds.map(m => (
            <div key={m.id} className="cycleMedItem" onClick={() => openEdit(m)}>
              <div className="cycleMedInfo">
                <span className="cycleMedName">💊 {m.name}</span>
                {m.dosage && <span className="cycleMedDose">{m.dosage}</span>}
                {m.reminder_time && <span className="cycleMedTime">⏰ {m.reminder_time}</span>}
              </div>
              <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                <button className="cycleMedEdit" onClick={e => { e.stopPropagation(); openEdit(m); }}>✏️</button>
                <button className="cycleMedDelete" onClick={e => { e.stopPropagation(); remove(m.id); }}>✕</button>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function CycleTrendChart({ points, color = "#8b5cf6", labelForPoint, normalRange }) {
  if (!points?.length) return null;
  const values = points.map(point => Number(point.value) || 0);
  const min = Math.min(...values, ...(normalRange || []));
  const max = Math.max(...values, ...(normalRange || []));
  const spread = Math.max(1, max - min);
  const coords = points.map((point, index) => ({
    ...point,
    x: points.length === 1 ? 50 : 8 + (index / (points.length - 1)) * 84,
    y: 76 - ((Number(point.value) - min) / spread) * 54,
  }));
  const smoothLine = coords.length === 1
    ? `M ${coords[0].x} ${coords[0].y}`
    : coords.reduce((path, point, index) => {
      if (index === 0) return `M ${point.x} ${point.y}`;
      if (index === coords.length - 1) return `${path} Q ${coords[index - 1].x} ${coords[index - 1].y} ${point.x} ${point.y}`;
      const next = coords[index + 1];
      return `${path} Q ${point.x} ${point.y} ${(point.x + next.x) / 2} ${(point.y + next.y) / 2}`;
    }, "");
  const area = `${smoothLine} L ${coords[coords.length - 1].x} 82 L ${coords[0].x} 82 Z`;
  const rangeTop = normalRange ? 76 - ((normalRange[1] - min) / spread) * 54 : null;
  const rangeBottom = normalRange ? 76 - ((normalRange[0] - min) / spread) * 54 : null;
  return (
    <div className="cycleTrendChart">
      <svg viewBox="0 0 100 90" role="img" aria-label={points.map(labelForPoint).join(", ")}>
        <defs><linearGradient id={`cycle-chart-${color.replace("#", "")}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={color} stopOpacity=".28"/><stop offset="1" stopColor={color} stopOpacity="0"/></linearGradient></defs>
        {[22,49,76].map(y => <line key={y} x1="7" y1={y} x2="93" y2={y} className="cycleChartGridLine" />)}
        {normalRange && <rect x="7" y={rangeTop} width="86" height={Math.max(2, rangeBottom - rangeTop)} rx="2" className="cycleChartNormalBand" />}
        <path d={area} fill={`url(#cycle-chart-${color.replace("#", "")})`} />
        <path d={smoothLine} fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        {coords.map((point, index) => (
          <g key={`${point.label}-${index}`} className="cycleChartPoint" tabIndex="0">
            <circle cx={point.x} cy={point.y} r="4.8" fill="transparent" />
            <circle cx={point.x} cy={point.y} r="2.2" fill={color} stroke="var(--c-card)" strokeWidth="1.2" />
            <text x={point.x} y={Math.max(9, point.y - 5)} textAnchor="middle" className="cycleChartValue">{point.value}</text>
            <title>{labelForPoint(point)}</title>
          </g>
        ))}
      </svg>
      <div className="cycleChartLabels">{points.map((point, index) => <span key={index}>{point.shortLabel}</span>)}</div>
      <div className="cycleChartScale"><span>{max}</span><span>{min}</span></div>
    </div>
  );
}

function CycleMetricPanel({ icon, label, value, percent, tone = "violet", detail }) {
  const safePercent = Math.min(100, Math.max(0, Number(percent) || 0));
  return (
    <div className={`cycleMetricPanel ${tone}`}>
      <div className="cycleMetricHeader"><span className="cycleMetricIcon"><CycleGlyph name={icon} size={20} /></span><span>{label}</span><b>{value}</b></div>
      <div className="cycleMetricTrack"><i style={{ width: `${safePercent}%` }} /></div>
      {detail && <small>{detail}</small>}
    </div>
  );
}

function CycleMoodDistribution({ values, moodLabel, totalLabel }) {
  const entries = Object.entries(values || {}).filter(([, count]) => Number(count) > 0);
  const total = entries.reduce((sum, [, count]) => sum + Number(count), 0) || 1;
  const colors = ["#8b6cf6", "#f34f7d", "#38c98a", "#38bdf8", "#fb923c", "#a78bfa", "#64748b"];
  let offset = 0;
  return (
    <div className="cycleMoodOverview">
      <svg className="cycleMoodRing" viewBox="0 0 42 42" role="img">
        <circle cx="21" cy="21" r="15.9" className="cycleMoodRingBase" />
        {entries.map(([key, count], index) => {
          const size = Number(count) / total * 100;
          const node = <circle key={key} cx="21" cy="21" r="15.9" fill="none" stroke={colors[index % colors.length]} strokeWidth="5" strokeDasharray={`${size} ${100 - size}`} strokeDashoffset={-offset} transform="rotate(-90 21 21)" />;
          offset += size;
          return node;
        })}
        <text x="21" y="20" textAnchor="middle" className="cycleMoodRingValue">{total}</text>
        <text x="21" y="25" textAnchor="middle" className="cycleMoodRingLabel">{totalLabel}</text>
      </svg>
      <div className="cycleMoodDist">{entries.map(([key, count], index) => <div key={key} className="cycleMoodDistItem"><span className="cycleMoodColor" style={{ background: colors[index % colors.length] }} /><MoodIcon mood={key} size={24} /><span>{moodLabel(key)}</span><b>{count}</b></div>)}</div>
    </div>
  );
}

// ── Analysis Section ──────────────────────────────────────────
function CycleAnalysisSection({ t, lang }) {
  const [range, setRange] = useState("6m");
  const [data, setData] = useState(() => cycleAnalysisMemoryCache?.["6m"]?.data || null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const cached = cycleAnalysisMemoryCache?.[range]?.data;
    if (cached) setData(cached);
    setRefreshing(true);
    getCycleAnalysis(range)
      .then(res => {
        if (res.ok) {
          setData(res);
          writeCycleAnalysisCache(range, res);
        }
      })
      .finally(() => setRefreshing(false));
  }, [range]);

  const moodLabel = (key) => MOODS.find(m => m.key === key)?.[lang] || key;

  const hasEarlyAnalysis = data && (data.periodCount > 0 || data.entryCount > 0 || data.predictions?.lastPeriodStart);

  return (
    <div className="cycleAnalysis">
      <div className="cycleSectionHeader">
        <div className="cycleSectionTitle">{t("cycle.analysis.title")}</div>
        <div className="cycleRangePicker">
          {["3m","6m","1y"].map(r => (
            <button key={r} className={`cycleRangeBtn ${range === r ? "active" : ""}`} onClick={() => setRange(r)}>
              {r === "3m" ? (lang === "en" ? "3M" : "3A") : r === "6m" ? (lang === "en" ? "6M" : "6A") : (lang === "en" ? "1Y" : "1Y")}
            </button>
          ))}
        </div>
      </div>

      {refreshing && <div className="cycleInlineUpdate">{lang === "en" ? "Updating in the background…" : "Arka planda güncelleniyor…"}</div>}

      {hasEarlyAnalysis ? (
        <>
          {/* Özet istatistikler */}
          <div className="cycleStatGrid">
            <div className="cycleStatCard">
              <span>{t("cycle.analysis.periodCount")}</span>
              <b>{data.periodCount || 0}</b>
            </div>
            <div className="cycleStatCard">
              <span>{t("cycle.analysis.avgCycle")}</span>
              <b>{data.predictions?.avgCycleLength || "–"} {t("cycle.days")}</b>
            </div>
            <div className="cycleStatCard">
              <span>{t("cycle.analysis.avgPeriod")}</span>
              <b>{data.predictions?.avgPeriodLength || "–"} {t("cycle.days")}</b>
            </div>
            <div className="cycleStatCard">
              <span>{t("cycle.analysis.regularity")}</span>
              <b>{data.predictions?.regularityScore != null ? `%${data.predictions.regularityScore}` : "–"}</b>
            </div>
          </div>

          <div className="cycleAnalysisPremiumGrid">
            <div className="cycleAnalysisInsightCard primary">
              <span className="cycleAnalysisSvgIcon"><CycleGlyph name="activity" /></span>
              <div><b>{t("cycle.analysis.healthScore")}</b><p>{data.predictions?.regularityScore != null ? (data.predictions.regularityScore >= 80 ? t("cycle.analysis.scoreGood") : data.predictions.regularityScore >= 60 ? t("cycle.analysis.scoreMedium") : t("cycle.analysis.scoreLow")) : t("cycle.analysis.scoreStart")}</p></div>
            </div>
            <div className="cycleAnalysisInsightCard">
              <span className="cycleAnalysisSvgIcon rose"><CycleGlyph name="calendar" /></span>
              <div><b>{t("cycle.analysis.nextWindow")}</b><p>{data.predictions?.nextPeriodDate ? formatDate(data.predictions.nextPeriodDate, lang) : t("cycle.analysis.notEnough")}</p></div>
            </div>
            <div className="cycleAnalysisInsightCard">
              <span className="cycleAnalysisSvgIcon violet"><CycleGlyph name="spark" /></span>
              <div><b>{t("cycle.analysis.ovulationWindow")}</b><p>{data.predictions?.ovulationDate ? formatDate(data.predictions.ovulationDate, lang) : t("cycle.analysis.notEnough")}</p></div>
            </div>
          </div>

          <div className="cycleMetricGrid">
            <CycleMetricPanel icon="activity" label={t("cycle.analysis.regularity")} value={data.predictions?.regularityScore != null ? `%${data.predictions.regularityScore}` : "—"} percent={data.predictions?.regularityScore || 0} detail={data.predictions?.regularityScore >= 80 ? t("cycle.analysis.scoreGood") : t("cycle.analysis.scoreMedium")} />
            <CycleMetricPanel icon="drop" label={t("cycle.analysis.avgPeriod")} value={`${data.predictions?.avgPeriodLength || "—"} ${t("cycle.days")}`} percent={((data.predictions?.avgPeriodLength || 0) / 10) * 100} tone="rose" />
          </div>

          {/* Döngü uzunluğu trendi */}
          {data.cycleLengths?.length > 0 && (
            <div className="cycleChartSection">
              <div className="cycleChartTitle">{t("cycle.analysis.cycleTrend")}</div>
              <CycleTrendChart color="#8b5cf6" normalRange={[21, 35]} points={data.cycleLengths.slice(-8).map(cl => ({ value: cl.length, label: formatDate(cl.period, lang), shortLabel: formatDate(cl.period, lang).split(" ")[0] }))} labelForPoint={point => `${point.label}: ${point.value} ${t("cycle.days")}`} />
            </div>
          )}

          {data.periodDurations?.length > 0 && (
            <div className="cycleChartSection">
              <div className="cycleChartTitle">{t("cycle.analysis.periodTrend")}</div>
              <div className="cycleDurationBars">{data.periodDurations.slice(-8).map((item, index) => { const value = Number(item.length ?? item.duration ?? item.value) || 0; return <div className="cycleDurationBarItem" key={item.period || index}><b>{value}</b><i style={{ height: `${Math.max(16, Math.min(100, value * 14))}%` }} /><span>{formatDate(item.period || item.date, lang).split(" ")[0]}</span></div>; })}</div>
            </div>
          )}

          {/* Ağrı trendi */}
          {data.painTrend?.length > 0 && (
            <div className="cycleChartSection">
              <div className="cycleChartTitle">{t("cycle.analysis.painTrend")}</div>
              <CycleTrendChart color="#ef476f" points={data.painTrend.slice(-8).map(p => ({ value: p.avg, label: p.month, shortLabel: p.month.slice(5) }))} labelForPoint={point => `${point.label}: ${point.value}/10`} />
            </div>
          )}

          {/* Ruh hali dağılımı */}
          {Object.keys(data.moodDistribution || {}).length > 0 && (
            <div className="cycleChartSection">
              <div className="cycleChartTitle">{t("cycle.analysis.moodDist")}</div>
              <CycleMoodDistribution values={data.moodDistribution} moodLabel={moodLabel} totalLabel={lang === "en" ? "entries" : "kayıt"} />
            </div>
          )}

          {/* Top belirtiler */}
          {data.topSymptoms?.length > 0 && (
            <div className="cycleChartSection">
              <div className="cycleChartTitle">{t("cycle.analysis.topSymptoms")}</div>
              {data.topSymptoms.map((s, i) => {
                const sym = SYMPTOMS_LIST.find(x => x.key === s.key);
                const maxCount = data.topSymptoms[0]?.count || 1;
                return (
                  <div key={i} className="cycleSymptomBar">
                    <span className="cycleSymptomBarLabel"><CycleGlyph name="symptom" size={17} />{sym?.[lang] || sym?.tr || s.key}</span>
                    <div className="cycleSymptomBarTrack">
                      <div className="cycleSymptomBarFill" style={{ width: `${(s.count / maxCount) * 100}%` }} />
                    </div>
                    <span>{s.count}</span>
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : (
        /* Boş durum — yeni kullanıcı için premium guide */
        <div className="cycleEmptyAnalysis">
          <div className="cycleEmptyAnalysisIcon"><CycleGlyph name="chart" size={34} /></div>
          <div className="cycleEmptyAnalysisTitle">{t("cycle.analysis.empty.title")}</div>
          <div className="cycleEmptyAnalysisDesc">{t("cycle.analysis.empty.desc")}</div>
          <div className="cycleAnalysisStarterChart" aria-hidden="true">
            {[42, 58, 76, 62, 84].map((h, i) => <i key={i} style={{height: `${h}%`}} />)}
          </div>
          <div className="cycleEmptySteps">
            <div className="cycleEmptyStep"><span className="cycleEmptyStepNum">1</span><span>{t("cycle.analysis.empty.s1")}</span></div>
            <div className="cycleEmptyStepLine" />
            <div className="cycleEmptyStep"><span className="cycleEmptyStepNum">2</span><span>{t("cycle.analysis.empty.s2")}</span></div>
            <div className="cycleEmptyStepLine" />
            <div className="cycleEmptyStep"><span className="cycleEmptyStepNum">3</span><span>{t("cycle.analysis.empty.s3")}</span></div>
          </div>
          {data?.predictions && (
            <div className="cycleEmptyCurrentCycle">
              <div className="cycleEmptyCurrentTitle">{t("cycle.analysis.insights")}</div>
              <div className="cycleStatGrid" style={{ marginTop: 8 }}>
                <div className="cycleStatCard">
                  <span>{t("cycle.hero.today")}</span>
                  <b>{data.predictions.currentCycleDay || "–"}</b>
                </div>
                <div className="cycleStatCard">
                  <span>{t("cycle.analysis.avgCycle")}</span>
                  <b>{data.predictions.avgCycleLength} {t("cycle.days")}</b>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Settings Panel ────────────────────────────────────────────
const CYCLE_DEFAULT_TIMES = {
  notif_daily_log_time: "09:00",
  notif_period_7d_time: "09:00",
  notif_period_3d_time: "09:00",
  notif_period_1d_time: "09:00",
  notif_period_today_time: "09:00",
  notif_ovulation_time: "13:00",
  notif_fertile_time: "13:00",
  notif_pms_time: "13:00",
  notif_delay_time: "13:00",
};

function normalizeCycleTime(value, fallback) {
  const raw = typeof value === "string" ? value.slice(0, 5) : "";
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(raw) ? raw : fallback;
}

function normalizeCycleSettings(settings = {}) {
  const normalized = { ...settings };
  Object.entries(CYCLE_DEFAULT_TIMES).forEach(([key, fallback]) => {
    normalized[key] = normalizeCycleTime(normalized[key], fallback);
  });
  return normalized;
}

function CycleSettingsPanel({ t, lang, onClose }) {
  const [settings, setSettings] = useState(null);
  const [settingsTab, setSettingsTab] = useState("toggles");
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    getCycleSettings().then(res => { if (res.ok) setSettings(normalizeCycleSettings(res.settings)); });
  }, []);

  const update = (key, val) => setSettings(prev => ({ ...prev, [key]: val }));

  const save = async () => {
    setSaving(true);
    try {
      await saveCycleSettings(normalizeCycleSettings(settings));
      onClose();
    } finally { setSaving(false); }
  };

  const handleExport = async (format) => {
    try {
      const res = await exportCycleData(format);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `ozel-takvim.${format}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (scope, params = {}) => {
    try {
      const res = await deleteCycleData({ scope, ...params });
      if (res?.ok === false) throw new Error(res.error || "delete_failed");
      localStorage.removeItem("cycle_setup_done");
      setDeleteConfirm(null);
      onClose({ deleted: true });
    } catch (e) {
      console.error(e);
      setDeleteConfirm(null);
    }
  };

  if (!settings) return <div className="cyclePanelLoading"><div className="cycleSpinner" /></div>;

  const toggle = (key) => update(key, !settings[key]);

  return (
    <div className="cycleSettingsPanel">
      <div className="cycleDayPanelHeader">
        <span>{t("cycle.settings.title")}</span>
        <button onClick={onClose}>✕</button>
      </div>
      <div className="cycleSettingsBody">
        <div className="cycleSettingsTabs" role="tablist" aria-label={t("cycle.settings.title")}>
          <button
            type="button"
            className={`cycleSettingsTab ${settingsTab === "toggles" ? "active" : ""}`}
            onClick={() => setSettingsTab("toggles")}
          >
            {t("cycle.settings.notifications")}
          </button>
          <button
            type="button"
            className={`cycleSettingsTab ${settingsTab === "times" ? "active" : ""}`}
            onClick={() => setSettingsTab("times")}
          >
            {t("cycle.settings.notificationTimes")}
          </button>
        </div>

        {settingsTab === "toggles" && (
          <>
            {/* Bildirimler */}
            <div className="cycleSettingGroup">
              <div className="cycleSettingGroupTitle">{t("cycle.settings.notifications")}</div>
              {[
                ["notif_period_7d",   t("cycle.notif.period7d")],
                ["notif_period_3d",   t("cycle.notif.period3d")],
                ["notif_period_1d",   t("cycle.notif.period1d")],
                ["notif_period_today",t("cycle.notif.periodToday")],
                ["notif_ovulation",   t("cycle.notif.ovulation")],
                ["notif_fertile",     t("cycle.notif.fertile")],
                ["notif_pms",         t("cycle.notif.pms")],
                ["notif_daily_log",   t("cycle.notif.dailyLog")],
                ["notif_delay",       t("cycle.notif.delay")],
                ["notif_medication",  t("cycle.notif.medication")],
              ].map(([key, label]) => (
                <div key={key} className="cycleSettingRow">
                  <span>{label}</span>
                  <button className={`cycleToggle ${settings[key] ? "on" : ""}`} onClick={() => toggle(key)}>
                    <span className="cycleToggleThumb" />
                  </button>
                </div>
              ))}
            </div>

            {/* Gizlilik */}
            <div className="cycleSettingGroup">
              <div className="cycleSettingGroupTitle">{t("cycle.settings.privacy")}</div>
              <div className="cycleSettingRow">
                <span>{t("cycle.notif.privateMode")}</span>
                <button className={`cycleToggle ${settings.private_notif_mode ? "on" : ""}`} onClick={() => toggle("private_notif_mode")}>
                  <span className="cycleToggleThumb" />
                </button>
              </div>
              <div className="cycleSettingRow">
                <span>{t("cycle.settings.sexualTracking")}</span>
                <button className={`cycleToggle ${settings.sexual_tracking_enabled ? "on" : ""}`} onClick={() => toggle("sexual_tracking_enabled")}>
                  <span className="cycleToggleThumb" />
                </button>
              </div>
              <div className="cycleSettingRow">
                <span>{t("cycle.settings.pregnancyMode")}</span>
                <button className={`cycleToggle ${settings.pregnancy_planning_mode ? "on" : ""}`} onClick={() => toggle("pregnancy_planning_mode")}>
                  <span className="cycleToggleThumb" />
                </button>
              </div>
            </div>

            {/* Dışa Aktarma */}
            <div className="cycleSettingGroup">
              <div className="cycleSettingGroupTitle">{t("cycle.settings.export")}</div>
              <div className="cycleExportBtns">
                <button className="cycleSecondaryBtn" onClick={() => handleExport("json")}>JSON</button>
                <button className="cycleSecondaryBtn" onClick={() => handleExport("csv")}>CSV</button>
              </div>
            </div>

            {/* Veri Silme */}
            <div className="cycleSettingGroup">
              <div className="cycleSettingGroupTitle cycleSettingDangerTitle">{t("cycle.settings.deleteData")}</div>
              <button className="cycleDangerBtn" onClick={() => setDeleteConfirm("all")}>
                {t("cycle.settings.deleteAll")}
              </button>
            </div>
          </>
        )}

        {settingsTab === "times" && (
          <div className="cycleSettingGroup cycleTimeGroup">
            <div className="cycleSettingGroupTitle">{t("cycle.settings.notificationTimes")}</div>
            <p className="cycleSettingHint">{t("cycle.settings.notificationTimesHint")}</p>
            {[
              ["notif_daily_log_time", t("cycle.notif.dailyLog"), "09:00"],
              ["notif_period_7d_time", t("cycle.notif.period7d"), "09:00"],
              ["notif_period_3d_time", t("cycle.notif.period3d"), "09:00"],
              ["notif_period_1d_time", t("cycle.notif.period1d"), "09:00"],
              ["notif_period_today_time", t("cycle.notif.periodToday"), "09:00"],
              ["notif_ovulation_time", t("cycle.notif.ovulation"), "13:00"],
              ["notif_fertile_time", t("cycle.notif.fertile"), "13:00"],
              ["notif_pms_time", t("cycle.notif.pms"), "13:00"],
              ["notif_delay_time", t("cycle.notif.delay"), "13:00"],
            ].map(([key, label, fallback]) => (
              <label key={key} className="cycleTimeRow">
                <span>
                  <b>{label}</b>
                  <small>{t("cycle.settings.defaultTime").replace("{time}", fallback)}</small>
                </span>
                <input
                  type="time"
                  className="cycleTimeInput"
                  value={normalizeCycleTime(settings[key], fallback)}
                  onChange={(e) => update(key, e.target.value)}
                />
              </label>
            ))}
          </div>
        )}

        {deleteConfirm && (
          <div className="cycleDeleteConfirm">
            <p>{t("cycle.settings.deleteConfirm")}</p>
            <div className="cycleOnboardingBtns">
              <button className="cycleSecondaryBtn" onClick={() => setDeleteConfirm(null)}>{t("common.cancel")}</button>
              <button className="cycleDangerBtn" onClick={() => handleDelete(deleteConfirm)}>{t("common.confirm")}</button>
            </div>
          </div>
        )}

        <button className="cyclePrimaryBtn" onClick={save} disabled={saving}>
          {saving ? "..." : t("common.save")}
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  ANA COMPONENT
// ══════════════════════════════════════════════════════════════
export default function OzelTakvimim({ onGeri }) {
  const { t, lang } = useLanguage();
  const cachedPage = readCyclePageCache();

  const [loading, setLoading] = useState(() => !cachedPage?.loaded);
  const [refreshing, setRefreshing] = useState(false);
  // localStorage fallback: API başarısız olsa bile onboarding tekrar gösterilmez
  const [setupDone, setSetupDone] = useState(() => {
    if (typeof cachedPage?.setupDone === "boolean") return cachedPage.setupDone;
    return localStorage.getItem("cycle_setup_done") === "true";
  });
  const [predictions, setPredictions] = useState(() => cachedPage?.predictions || null);
  const [insights, setInsights] = useState(() => cachedPage?.insights || []);
  const [calYear, setCalYear] = useState(() => cachedPage?.calYear || new Date().getFullYear());
  const [calMonth, setCalMonth] = useState(() => cachedPage?.calMonth || new Date().getMonth() + 1);
  const [calData, setCalData] = useState(() => cachedPage?.calData || null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [periodModal, setPeriodModal] = useState(null); // "start" | "end"
  const [showSettings, setShowSettings] = useState(false);
  const [activeTab, setActiveTab] = useState("home"); // home | calendar | analysis | medications
  const TABS = ["home", "calendar", "analysis", "medications"];
  const swipeRef = React.useRef({ startX: 0, startY: 0, active: false });

  useEffect(() => {
    if (!selectedDay && !periodModal && !showSettings) return undefined;
    const scrollY = window.scrollY;
    const previous = {
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
      overflow: document.body.style.overflow,
    };
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.position = previous.position;
      document.body.style.top = previous.top;
      document.body.style.width = previous.width;
      document.body.style.overflow = previous.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [selectedDay, periodModal, showSettings]);

  const onContentTouchStart = (e) => {
    if (e.target?.closest?.(".cycleCal")) {
      swipeRef.current = { startX: 0, startY: 0, active: false };
      return;
    }
    swipeRef.current = { startX: e.touches[0].clientX, startY: e.touches[0].clientY, active: true };
  };
  const onContentTouchEnd = (e) => {
    if (!swipeRef.current.active) return;
    const dx = e.changedTouches[0].clientX - swipeRef.current.startX;
    const dy = Math.abs(e.changedTouches[0].clientY - swipeRef.current.startY);
    swipeRef.current.active = false;
    if (Math.abs(dx) < 60 || dy > 80) return;
    const idx = TABS.indexOf(activeTab);
    if (dx < 0 && idx < TABS.length - 1) setActiveTab(TABS[idx + 1]);
    if (dx > 0 && idx > 0) setActiveTab(TABS[idx - 1]);
  };

  const [settings, setSettings] = useState(() => cachedPage?.settings || null);
  const loadAll = useCallback(async (options = {}) => {
    const silent = options.silent === true || readCyclePageCache()?.loaded;
    if (!silent) setLoading(true);
    else setRefreshing(true);
    const withTimeout = (p) => Promise.race([
      p,
      new Promise((_, rej) => setTimeout(() => rej(new Error("timeout")), 10000)),
    ]);
    const nextCache = {};
    try {
      const [settingsRes, predRes, insRes] = await Promise.allSettled([
        withTimeout(getCycleSettings()),
        withTimeout(getCyclePredictions()),
        withTimeout(getCycleInsights(lang)),
      ]);
      if (settingsRes.status === "fulfilled" && settingsRes.value?.ok) {
        const s = settingsRes.value.settings;
        const done = s?.setup_completed === true;
        setSetupDone(done);
        setSettings(s);
        nextCache.setupDone = done;
        nextCache.settings = s;
        if (done) localStorage.setItem("cycle_setup_done", "true");
      }
      if (predRes.status === "fulfilled" && predRes.value?.ok) {
        setPredictions(predRes.value.predictions);
        nextCache.predictions = predRes.value.predictions;
      }
      if (insRes.status === "fulfilled" && insRes.value?.ok) {
        const nextInsights = insRes.value.insights || [];
        setInsights(nextInsights);
        nextCache.insights = nextInsights;
      }
      writeCyclePageCache(nextCache);
    } catch (e) {
      console.error("loadAll error:", e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [lang]);

  const loadCalendar = useCallback(async () => {
    try {
      const res = await getCalendarMonth(calYear, calMonth);
      if (res.ok) {
        setCalData(res);
        writeCyclePageCache({ calData: res, calYear, calMonth });
      }
    } catch (e) { console.error(e); }
  }, [calYear, calMonth]);

  useEffect(() => { loadAll({ silent: !!readCyclePageCache()?.loaded }); }, [loadAll]);
  useEffect(() => { if (activeTab === "calendar") loadCalendar(); }, [activeTab, calYear, calMonth, loadCalendar]);

  const handleMonthChange = (delta) => {
    let m = calMonth + delta;
    let y = calYear;
    if (m > 12) { m = 1; y++; }
    if (m < 1) { m = 12; y--; }
    setCalMonth(m); setCalYear(y);
  };

  const handleQuickAction = (action) => {
    if (action === "start") setPeriodModal("start");
    else if (action === "end") setPeriodModal("end");
    else if (action === "history") setPeriodModal("history");
    else if (action === "symptom" || action === "note") setSelectedDay(today());
  };

  if (loading && !readCyclePageCache()?.loaded) {
    return (
      <div className="cycleLoadingScreen">
        <div className="cycleSpinner large" />
        <p>{t("common.loading")}</p>
      </div>
    );
  }

  if (!setupDone) {
    return (
      <CycleOnboarding
        t={t} lang={lang}
        onComplete={() => {
          localStorage.setItem("cycle_setup_done", "true");
          setSetupDone(true);
          loadAll({ silent: true });
        }}
      />
    );
  }

  return (
    <div className="cyclePageRoot">
      {/* Header */}
      <div className="cycleTopShell">
        <div className="cyclePageHeader">
          <button className="cycleBackBtn" onClick={onGeri} aria-label={t("common.back")}>‹</button>
          <div className="cycleHeaderIcon"><img src={healthPrivateCalendarAsset} alt="" aria-hidden="true" draggable="false" /></div>
          <div className="cycleHeaderText">
            <div className="cyclePageTitle">{t("cycle.title")}</div>
            <div className="cyclePageSubtitle">🔒 {t("cycle.private")}</div>
          </div>
          <button className="cycleSettingsBtn" onClick={() => setShowSettings(true)} aria-label={t("cycle.settings.title")}><CycleGlyph name="settings" /></button>
        </div>

      {/* Tab Bar */}
      <div className="cycleTabBar">
        {[
          { id: "home",        label: t("cycle.tab.home")     },
          { id: "calendar",    label: t("cycle.tab.calendar") },
          { id: "analysis",    label: t("cycle.tab.analysis") },
          { id: "medications", label: t("cycle.tab.meds")     },
        ].map(tab => (
          <button key={tab.id}
            className={`cycleTab ${activeTab === tab.id ? "active" : ""}`}
            onClick={() => setActiveTab(tab.id)}>
            {tab.label}
          </button>
        ))}
      </div>
      </div>

      {refreshing && <div className="cycleRefreshPill">{lang === "en" ? "Updating…" : "Güncelleniyor…"}</div>}

      {/* İçerik */}
      <div className="cycleContent" onTouchStart={onContentTouchStart} onTouchEnd={onContentTouchEnd}>
        {activeTab === "home" && (
          <>
            <CycleHeroCard
              predictions={predictions}
              t={t} lang={lang}
              onQuickAction={handleQuickAction}
            />
            {/* Hamilelik planlama modu aktifse banner */}
            {settings?.pregnancy_planning_mode && predictions?.fertileStart && (
              <div className="cyclePregnancyBanner">
                <div className="cyclePregnancyBannerIcon">🌷</div>
                <div>
                  <div className="cyclePregnancyBannerTitle">{t("cycle.pregnancy.banner")}</div>
                  <div className="cyclePregnancyBannerSub">
                    {t("cycle.hero.fertile")}: {predictions.fertileStart && formatDate(predictions.fertileStart, lang)} – {predictions.fertileEnd && formatDate(predictions.fertileEnd, lang)}
                  </div>
                  <div className="cyclePregnancyBannerTip">{t("cycle.pregnancy.tip")}</div>
                </div>
              </div>
            )}
            <CycleInsightsSection insights={insights} t={t} predictions={predictions} />
          </>
        )}

        {activeTab === "calendar" && (
          <>
            <CycleCalendar
              year={calYear} month={calMonth}
              calData={calData} predictions={predictions}
              onDayClick={setSelectedDay}
              onMonthChange={handleMonthChange}
              t={t} lang={lang}
            />
            {predictions && (
              <div className="cycleCalInfo">
                <div className="cycleCalInfoTitle">{lang === "en" ? "This cycle" : "Bu döngü"}</div>
                <div className="cycleCalInfoGrid">
                  <div className="cycleCalInfoItem">
                    <span>{lang === "en" ? "Cycle day" : "Döngü günü"}</span>
                    <b style={{color:"#6366f1"}}>{predictions.currentCycleDay || "–"}</b>
                  </div>
                  <div className="cycleCalInfoItem">
                    <span>{lang === "en" ? "Phase" : "Faz"}</span>
                    <b style={{color: PHASES[predictions.currentPhase]?.color || "#64748b"}}>
                      {PHASES[predictions.currentPhase]?.label[lang] || PHASES[predictions.currentPhase]?.label.tr || "–"}
                    </b>
                  </div>
                  <div className="cycleCalInfoItem">
                    <span>{lang === "en" ? "Next period" : "Sonraki regl"}</span>
                    <b style={{color:"#ef476f"}}>{predictions.nextPeriodDate ? formatDate(predictions.nextPeriodDate, lang) : "–"}</b>
                  </div>
                  <div className="cycleCalInfoItem">
                    <span>{lang === "en" ? "Ovulation" : "Yumurtlama"}</span>
                    <b style={{color:"#8b5cf6"}}>{predictions.ovulationDate ? formatDate(predictions.ovulationDate, lang) : "–"}</b>
                  </div>
                  {predictions.fertileStart && (
                    <div className="cycleCalInfoItem" style={{gridColumn:"1/-1"}}>
                      <span>{lang === "en" ? "Fertile window" : "Doğurgan dönem"}</span>
                      <b style={{color:"#22c55e"}}>{formatDate(predictions.fertileStart, lang)} – {formatDate(predictions.fertileEnd, lang)}</b>
                    </div>
                  )}
                </div>
                <p className="cycleCalInfoHint">
                  {lang === "en" ? "Tap any day on the calendar to add symptoms, mood, or notes." : "Belirti, ruh hali veya not eklemek için takvimden herhangi bir güne dokunun."}
                </p>
              </div>
            )}
          </>
        )}

        {activeTab === "analysis" && (
          <CycleAnalysisSection t={t} lang={lang} />
        )}

        {activeTab === "medications" && (
          <CycleMedicationsSection t={t} lang={lang} />
        )}
      </div>

      {/* Gün Detayı Panel */}
      {selectedDay && (
        <div className="cycleModalOverlay" onClick={e => e.target === e.currentTarget && setSelectedDay(null)}>
          <div className="cycleDayPanelWrap">
            <DayDetailPanel
              date={selectedDay} t={t} lang={lang}
              settings={settings}
              onClose={() => setSelectedDay(null)}
              onSaved={() => { loadAll({ silent: true }); loadCalendar(); }}
            />
          </div>
        </div>
      )}

      {/* Regl Modal */}
      {periodModal && (
        <PeriodStartModal
          mode={periodModal} t={t} lang={lang}
          onClose={() => setPeriodModal(null)}
          onSaved={() => { loadAll({ silent: true }); loadCalendar(); setPeriodModal(null); }}
        />
      )}

      {/* Ayarlar */}
      {showSettings && (
        <div className="cycleModalOverlay" onClick={e => e.target === e.currentTarget && setShowSettings(false)}>
          <div className="cycleDayPanelWrap">
            <CycleSettingsPanel t={t} lang={lang} onClose={(result) => {
              setShowSettings(false);
              if (result?.deleted) {
                setPredictions(null);
                setInsights([]);
                setCalData(null);
                setSetupDone(false);
                onGeri?.();
              } else {
                loadAll({ silent: true });
                loadCalendar();
              }
            }} />
          </div>
        </div>
      )}
    </div>
  );
}
